/**
 * Tracked Supabase Client
 *
 * Wraps Supabase client methods with automatic error tracking and request ID correlation.
 * Use this instead of the raw supabase client for edge function calls.
 */

import { supabase } from '@/lib/supabase/client';
import { trackEvent } from '@/lib/posthog';
import { errorEvents } from '@/lib/analytics/events/errors';
import { generateRequestId, getErrorContext, sanitizeErrorData } from '@/lib/analytics/errorContext';
import { logger } from '@/lib/logger';

/**
 * Options for tracked edge function invocation
 */
interface TrackedInvokeOptions {
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  // Skip error tracking for this call
  skipTracking?: boolean;
}

/**
 * Result of a tracked edge function call
 */
interface TrackedInvokeResult<T> {
  data: T | null;
  error: Error | null;
  requestId: string;
  durationMs: number;
}

/**
 * Invoke an edge function with automatic error tracking and request ID correlation
 *
 * @param functionName - Name of the edge function
 * @param options - Invocation options (body, headers)
 * @returns Promise with data, error, requestId, and duration
 *
 * @example
 * const { data, error, requestId } = await trackedInvoke('generate-strategy', {
 *   body: { user_id: '123' }
 * });
 */
export async function trackedInvoke<T = unknown>(
  functionName: string,
  options: TrackedInvokeOptions = {}
): Promise<TrackedInvokeResult<T>> {
  // Using singleton supabase
  const requestId = generateRequestId();
  const startTime = Date.now();

  // Merge request ID into headers
  const headers = {
    ...options.headers,
    'X-Request-ID': requestId,
  };

  try {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: options.body,
      headers,
    });

    const durationMs = Date.now() - startTime;

    if (error && !options.skipTracking) {
      // Track the error
      const errorEvent = errorEvents.edgeFunctionError(
        functionName,
        error.message,
        durationMs
      );

      trackEvent(errorEvent.event, {
        ...errorEvent.properties,
        request_id: requestId,
        ...getErrorContext(requestId),
      });

      // Log in development
      if (import.meta.env.DEV) {
        logger.error(`[trackedInvoke] ${functionName} error:`, error);
      }
    }

    return {
      data: data as T,
      error: error ? new Error(error.message) : null,
      requestId,
      durationMs,
    };
  } catch (err) {
    const durationMs = Date.now() - startTime;
    const error = err instanceof Error ? err : new Error(String(err));

    if (!options.skipTracking) {
      // Track the error
      const errorEvent = errorEvents.edgeFunctionError(
        functionName,
        error.message,
        durationMs
      );

      trackEvent(errorEvent.event, {
        ...errorEvent.properties,
        request_id: requestId,
        error_type: 'exception',
        ...getErrorContext(requestId),
      });

      // Log in development
      if (import.meta.env.DEV) {
        logger.error(`[trackedInvoke] ${functionName} exception:`, err);
      }
    }

    return {
      data: null,
      error,
      requestId,
      durationMs,
    };
  }
}

/**
 * Tracked Supabase table operations
 *
 * Wraps common Supabase table operations with error tracking.
 * Errors are automatically reported to PostHog.
 */
export const trackedSupabase = {
  /**
   * Invoke an edge function with tracking
   */
  functions: {
    invoke: trackedInvoke,
  },

  /**
   * Get raw supabase client for operations not yet wrapped
   * (Use with caution - errors won't be automatically tracked)
   */
  get raw() {
    return supabase;
  },
};

/**
 * Hook-friendly function to track Supabase query errors
 * Call this in your query/mutation error handlers
 */
export function trackSupabaseQueryError(
  operation: 'select' | 'insert' | 'update' | 'delete' | 'rpc',
  table: string,
  error: unknown,
  requestId?: string
): void {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorCode = (error as Record<string, unknown>)?.code as string | undefined;

  const errorEvent = errorEvents.supabaseError(
    operation,
    table,
    errorMessage,
    errorCode
  );

  trackEvent(errorEvent.event, {
    ...errorEvent.properties,
    request_id: requestId || generateRequestId(),
    ...getErrorContext(requestId),
  });
}

/**
 * Create a tracked mutation function for React Query
 *
 * @example
 * const mutation = useMutation({
 *   mutationFn: createTrackedMutation('update_profile', async (data) => {
 *     return await supabase.from('profiles').update(data);
 *   }),
 * });
 */
export function createTrackedMutation<TData, TVariables>(
  operationName: string,
  mutationFn: (variables: TVariables) => Promise<{ data: TData | null; error: Error | null }>
): (variables: TVariables) => Promise<TData> {
  return async (variables: TVariables) => {
    const requestId = generateRequestId();
    const startTime = Date.now();

    try {
      const { data, error } = await mutationFn(variables);
      const durationMs = Date.now() - startTime;

      if (error) {
        const errorEvent = errorEvents.supabaseError(
          'mutation',
          operationName,
          error.message,
          undefined
        );

        trackEvent(errorEvent.event, {
          ...errorEvent.properties,
          request_id: requestId,
          duration_ms: durationMs,
          ...getErrorContext(requestId),
        });

        throw error;
      }

      return data as TData;
    } catch (err) {
      const durationMs = Date.now() - startTime;
      const error = err instanceof Error ? err : new Error(String(err));

      // Only track if not already tracked above
      if (!(err instanceof Error && (err as unknown as Record<string, unknown>).__tracked)) {
        const errorEvent = errorEvents.supabaseError(
          'mutation',
          operationName,
          error.message,
          undefined
        );

        trackEvent(errorEvent.event, {
          ...errorEvent.properties,
          request_id: requestId,
          duration_ms: durationMs,
          error_type: 'exception',
          ...getErrorContext(requestId),
        });
      }

      throw err;
    }
  };
}

// Re-export the request ID generator for convenience
export { generateRequestId } from '@/lib/analytics/errorContext';
